// User related types
export enum UserRole {
  USER = "user",
  ADMIN = "admin",
  READER = "reader",
}

// Currency related types
export interface Currency {
  code: string;      // ISO 4217 code (EUR, USD, GBP, etc.)
  symbol: string;    // Currency symbol (€, $, £, etc.)
  name: string;      // Display name
  locale: string;    // Locale for number formatting (e.g., 'de-DE', 'en-US')
}

export const SUPPORTED_CURRENCIES: Currency[] = [
  { code: 'EUR', symbol: '€', name: 'Euro', locale: 'de-DE' },
  { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US' },
  { code: 'GBP', symbol: '£', name: 'British Pound', locale: 'en-GB' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', locale: 'de-CH' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP' },
];

export const DEFAULT_CURRENCY: Currency = SUPPORTED_CURRENCIES[0]!; // EUR (non-null assertion safe since array is never empty)

// Currency settings interface
export interface CurrencySettings {
  defaultCurrency: string; // ISO currency code (default: 'EUR')
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
}

// Project related types
export interface ProjectLocation {
  name: string;
  hourlyRate?: number; // hourly rate in project's currency, optional for billable projects
}

// Budget status for hourly budget tracking
export type BudgetStatus = 'over' | 'in' | 'under';

export interface Project {
  id: string;
  userId: string;
  name: string;
  description?: string;
  color?: string;
  status?: "active" | "archived";
  billable: boolean;
  currency?: string; // ISO currency code (EUR, USD, etc.), defaults to user's defaultCurrency
  hourlyRate?: number; // hourly rate in project's currency, optional for billable projects (default rate)
  budget?: number; // budget in project's currency or hours per day, optional for billable projects
  budgetType?: "monetary" | "hours"; // type of budget: monetary or daily hours
  budgetHourLimit?: number; // in minutes, tolerance for hourly budget compliance (default: 60 minutes = 1 hour)
  budgetExcludedDays?: number[]; // days excluded from hourly budget warnings (0=Sun…6=Sat), default [0,6]
  locations: ProjectLocation[]; // now contains location objects with optional hourly rates
  type?: "personal" | "shared";
  sharedUsers?: string[];
  totalDuration?: number; // total seconds tracked (server-computed, not stored in DB)
  lastRestoredAt?: string; // ISO timestamp set when status changes to 'active'; used by auto-archive
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  color?: string;
  billable?: boolean;
  currency?: string; // ISO currency code (EUR, USD, etc.)
  hourlyRate?: number; // hourly rate in project's currency, optional for billable projects (default rate)
  budget?: number; // budget in project's currency or hours per day, optional for billable projects
  budgetType?: "monetary" | "hours"; // type of budget: monetary or daily hours
  budgetHourLimit?: number; // in minutes, tolerance for hourly budget compliance (default: 60 minutes = 1 hour)
  budgetExcludedDays?: number[]; // days excluded from hourly budget warnings (0=Sun…6=Sat), default [0,6]
  locations?: ProjectLocation[]; // now contains location objects with optional hourly rates
  type?: "personal" | "shared";
  sharedUsers?: string[];
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  color?: string;
  status?: "active" | "archived";
  billable?: boolean;
  currency?: string; // ISO currency code (EUR, USD, etc.)
  hourlyRate?: number; // hourly rate in project's currency, optional for billable projects (default rate)
  budget?: number; // budget in project's currency or hours per day, optional for billable projects
  budgetType?: "monetary" | "hours"; // type of budget: monetary or daily hours
  budgetHourLimit?: number; // in minutes, tolerance for hourly budget compliance (default: 60 minutes = 1 hour)
  budgetExcludedDays?: number[]; // days excluded from hourly budget warnings (0=Sun…6=Sat), default [0,6]
  locations?: ProjectLocation[]; // now contains location objects with optional hourly rates
}

// Time entry related types
export interface TimeEntry {
  id: string;
  userId: string;
  projectId?: string;
  description: string;
  startTime: string;
  endTime?: string;
  duration?: number; // in seconds, calculated from start/end times
  isRunning: boolean;
  location?: string;
  timezone?: string; // IANA timezone identifier (e.g., "Europe/Berlin")
  createdAt: string;
  updatedAt: string;
}

export interface CreateTimeEntryRequest {
  description: string;
  projectId?: string;
  startTime: string;
  endTime?: string;
  location?: string;
  timezone?: string; // IANA timezone identifier (e.g., "Europe/Berlin")
}

export interface UpdateTimeEntryRequest {
  description?: string;
  projectId?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
}

export interface StopTimeEntryRequest {
  endTime: string;
}

// Calendar view types
export type CalendarView = "day" | "week" | "month" | "year";

export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  projectId: string;
  projectName: string;
  projectColor?: string;
  description: string;
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Authentication types
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatar?: string;
}

export interface LoginResponse {
  user: AuthUser;
  token: string;
  refreshToken?: string;
}

// Statistics types
export interface ProjectStats {
  projectId: string;
  projectName: string;
  totalTime: number; // in minutes
  timeEntries: number;
  averageSessionLength: number; // in minutes
}

export interface TimeStats {
  totalTime: number; // in minutes
  totalEntries: number;
  averageSessionLength: number; // in minutes
  projects: ProjectStats[];
}

// Report sorting types
export type ReportSortColumn =
  | "date"
  | "startTime"
  | "endTime"
  | "duration"
  | "project"
  | "location"
  | "description"
  | "pause" // day-based only
  | "timezone" // original timezone
  | "rate" // effective hourly rate (billable projects only)
  | "revenue"; // calculated revenue (billable projects only)

export type SortDirection = "asc" | "desc";

export interface ReportSortConfig {
  column: ReportSortColumn;
  direction: SortDirection;
}

// Valid sort columns for validation
export const VALID_SORT_COLUMNS: ReportSortColumn[] = [
  "date",
  "startTime",
  "endTime",
  "duration",
  "project",
  "location",
  "description",
  "pause",
  "timezone",
  "rate",
  "revenue",
];
